import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';

import Ad from '../Ad/Ad';
import { AdSizes } from '../Ad/AdSizes';
import CorusImage from '../CorusImage/CorusImage';
import addTabnabbing from '../../utils/addTabnabbing';
import useCurrentWidth from '../../utils/useCurrentWidth';
import FallbackImage from '../../inline-images/post-fallback-image.jpg';
import RightRail from '../RightRail/RightRail';

export default function SingleProfileHost({ author }) {
	const [profileDesc, setProfileDesc] = useState();
	const [showMore, setShowMore] = useState(false);
	const [isMobile, setIsMobile] = useState(false);

	const MAX_WORD_COUNT = 40;
	const MAX_WIDTH_MOBILE = 576;
	const width = useCurrentWidth();

	/**
	 * 
	 * @param {string} str Check if string is longer than MAX_WORD_COUNT or not
	 * @returns {bool}
	 */
	const hasLessThanMaxWords = (str) => {
		if (!str) return false;
		return !!(str.split(' ').length > MAX_WORD_COUNT);
	};

	const authorType = author?.details?.authorType.toLowerCase();
	const isStaff = authorType === 'staff';

	const getMaxWordsFromDesc = (desc) => {
		if (!showMore) return desc;
		return desc.split(' ').slice(0, MAX_WORD_COUNT).join(' ');
	};

	const computeShowMore = () => {
		if (width < MAX_WIDTH_MOBILE) {
			if (hasLessThanMaxWords(author?.content)) {
				setShowMore(true);
			}
		} else {
			setShowMore(false);
		}
	};

	useEffect(() => {
		computeShowMore();
		setProfileDesc(author?.content);
		setIsMobile(!!(width <= MAX_WIDTH_MOBILE));
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [author?.content, width]);

	const { details: { usersProfilePicture } } = author;

	const altText = usersProfilePicture?.altText || 'Flavour Network';
	const profilePic = usersProfilePicture?.sourceUrl || FallbackImage;
	const imageCredit = usersProfilePicture?.imageCredit || '';
	const title = usersProfilePicture?.title || '';

	const chefHostDetails = (
		<div className={`profile ${authorType}`}>
			<div className="profile-info">
				<h1 className="profile-name">{ author?.title }</h1>
			</div>
			<div className="profile-layout">
				<div className="profile-body">
					<div className="profile-image">
						<CorusImage
							alt={altText}
							className="profile-pic"
							height={565}
							src={profilePic}
							title={title}
							width={848}
						/>
					</div>
					<div className="profile-image-credit">{imageCredit}</div>
					<div className={`profile-desc expanded-${showMore ? 'false' : 'true'}`}>
						{addTabnabbing(profileDesc, getMaxWordsFromDesc)}
					</div>
					{(isMobile && hasLessThanMaxWords(author?.content)) && (
						<button
							className="profile-show-more"
							onClick={() => {
								setShowMore(!showMore);
							}}
							type="button"
						>
							{showMore ? 'Show more' : 'Show less'}
							<span className={`profile-show-more-${showMore ? 'true' : 'false'}`} />
						</button>
					)}
					<div className="profile-ad">
						<Ad
							key="profile-ad-2"
							sizes={{
								'xs sm md lg': AdSizes.BIG_BOX,
								'xl xxl': AdSizes.LEADERBOARD_BIGBOX_COMBO,
							}}
							htmlId="profile-ad-2"
							wrapperClass="profile-ad-2"
							className="profile-ad-2-wrapper"
							pos="2"
						/>
					</div>
				</div>
				<RightRail
					htmlId="profile-ad-1"
					wrapperClass="profile-ad-1-wrapper"
					className="profile-right-rail-ad"
				/>
			</div>
		</div>
	);

	const staffDetails = (
		<div className={`profile ${authorType}`}>
			<div className="staff-profile-pic">
				<CorusImage
					alt={altText}
					className="staff-profile-pic"
					height={100}
					src={profilePic}
					title={title}
					width={100}
				/>
			</div>
			<div className="staff-details-container">
				<div className="staff-profile">
					<h1 className="staff-profile-name">{ author?.title }</h1>
				</div>
				<div className="staff-profile-desc">
					{addTabnabbing(profileDesc)}
				</div>
			</div>
		</div>
	);

	return isStaff ? staffDetails : chefHostDetails;
}

SingleProfileHost.propTypes = {
	author: PropTypes.object.isRequired,
};
